import * as React from "react";

import { merge, $ } from "glamor";
import { formatMessage } from "lib/globalize-util";
import Fade from "@material-ui/core/Fade";
import { ExpandMore } from "components/icons/expand-more";
import { Chat } from "components/icons/chat";

import me from "models/me";
import { initZendesk } from "lib/zendesk"; // user zendesk login

const DELAY = 5000;

interface IProps {
    isRetailer: boolean;
    hide: boolean;
}

export const Zendesk: React.FunctionComponent<IProps> = ({ isRetailer, hide }) => {
    const [loaded, setLoaded] = React.useState(false);
    const [signedIn, setSignedIn] = React.useState(false);
    const [collapsed, setCollapsed] = React.useState(false);

    const initFunc = React.useCallback(() => {
        if (!loaded && me.isSignedIn()) {
            initZendesk({
                me,
                locale: me.locale,
                isRetailer,
                callback: () => {
                    setLoaded(true);
                },
            });
        }
        setSignedIn(me.isSignedIn());
    }, [me.isSignedIn()]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        // delay showing the widget for some time
        const timer = setTimeout(() => {
            initFunc();
        }, DELAY);

        me.on("sync logout", initFunc);

        return () => {
            clearTimeout(timer);
            me.off("sync logout", initFunc);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const styles = React.useMemo(() => {
        return merge(
            {
                position: "fixed",
                bottom: "200px",
                right: "0",
                height: "40px",
                lineHeight: "40px",
                width: "100px",
                transform: "rotate(-90deg)",
                transformOrigin: "100% 100%",
                zIndex: 10000,
                fontSize: "14px",
                fontWeight: "bold",
                letterSpacing: "0.18px",
                textAlign: "center",
                borderRadius: "12px 12px 0 0",
                background: "#5199DC",
                border: "2px solid #8AC7FE",
                borderBottom: 0,
                opacity: 0.8,
                transition: "opacity 0.3s ease !important",
                color: "white",
            },
            $(".collapsed", {
                height: "5px",
            }),
            $(":hover", {
                opacity: 1,
                transition: "opacity 0.3s ease !important",
            }),
            $(" .icon", {
                color: "white !important",
                width: "16px !important",
                height: "15px !important",
                position: "relative",
                top: "2px",
                marginRight: "8px",
            }),
            $(" .close", {
                color: "black !important",
                width: "16px !important",
                height: "15px !important",
                position: "absolute",
                top: "14px",
                left: "-20px",
                fontSize: "5px",
                padding: "0",
                border: "0",
                opacity: "0",
                transition: "opacity 0.3s ease !important",
            }),
            $(":hover .close", {
                opacity: "1",
                transition: "opacity 0.3s ease !important",
            })
        );
    }, []);

    const handleClick = React.useCallback(() => {
        if (collapsed) {
            setCollapsed(false);
        }
        (window as any).zE("webWidget", "open");
        (window as any).zE("webWidget", "show");
    }, [collapsed]);

    const handleCollapse: React.MouseEventHandler<HTMLButtonElement> = React.useCallback((e) => {
        setCollapsed(true);
        e.stopPropagation();
    }, []);

    if (!hide && signedIn && loaded) {
        return (
            <Fade timeout={500} in={true}>
                <div>
                    <button {...styles} className={(collapsed && "collapsed") || ""} onClick={handleClick}>
                        <ExpandMore className="close" onClick={handleCollapse} />
                        <Chat className="icon" />
                        {formatMessage("Help")}
                    </button>
                </div>
            </Fade>
        );
    }

    return null;
};
